import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../../api/axios";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";

const badgeColor = (status) => {
  if (["invalid", "rejected", "failed"].includes(status)) return "error";
  if (["approved", "applied"].includes(status)) return "success";
  if (status === "pending") return "warning";
  if (status === "draft") return "info";
  return "dark";
};

const typeColor = (type) => {
  if (["EXIT", "STOPPAGE"].includes(type)) return "error";
  if (["GRADE_STEP_CHANGE", "DEPARTMENT_CHANGE"].includes(type)) return "info";
  if (["ALLOWANCE_CHANGE", "ARREARS"].includes(type)) return "success";
  if (["DEDUCTION_CHANGE", "LOAN_CREATE", "LOAN_STOP"].includes(type)) return "warning";
  return "dark";
};

function SummaryCard({ icon, iconColor, label, value }) {
  return (
    <Card
      sx={{
        height: "100%",
        border: "1px solid #e5e7eb",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
      }}
    >
      <MDBox p={2.5}>
        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
          <Icon color={iconColor}>{icon}</Icon>
          <MDTypography variant="button" color="text" fontWeight="medium">
            {label}
          </MDTypography>
        </MDBox>

        <MDTypography variant="h5" fontWeight="bold">
          {value ?? "-"}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

SummaryCard.propTypes = {
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
};

SummaryCard.defaultProps = {
  icon: "info",
  iconColor: "info",
  label: "",
  value: "-",
};

function StatusCell({ value }) {
  return (
    <MDTypography
      variant="caption"
      color={badgeColor(value)}
      fontWeight="bold"
      sx={{ textTransform: "capitalize", letterSpacing: "0.2px" }}
    >
      {value}
    </MDTypography>
  );
}

StatusCell.propTypes = {
  value: PropTypes.string,
};

StatusCell.defaultProps = {
  value: "",
};

function TypeCell({ value }) {
  return (
    <MDTypography
      variant="caption"
      color={typeColor(value)}
      fontWeight="bold"
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.3px",
      }}
    >
      {value}
    </MDTypography>
  );
}

TypeCell.propTypes = {
  value: PropTypes.string,
};

TypeCell.defaultProps = {
  value: "",
};

function ErrorCell({ value }) {
  return value ? (
    <MDTypography variant="caption" color="error" fontWeight="regular">
      {value}
    </MDTypography>
  ) : (
    <MDTypography variant="caption" color="text">
      -
    </MDTypography>
  );
}

ErrorCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

ErrorCell.defaultProps = {
  value: "",
};

export default function VariationBatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(null);
  const [validationSummary, setValidationSummary] = useState(null);
  const [conflicts, setConflicts] = useState([]);

  const [pfno, setPfno] = useState("");
  const [variationType, setVariationType] = useState("");
  const [itemStatus, setItemStatus] = useState("");

  const [loading, setLoading] = useState(false);

  const loadBatch = async () => {
    const res = await axios.get("/variation-batches", { params: { per_page: 50 } });
    const foundBatch = (res.data?.data || []).find((row) => String(row.id) === String(id));
    setBatch(foundBatch || null);
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/variation-batches/${id}/items`, {
        params: {
          per_page: 200,
          pfno: pfno || undefined,
          variation_type: variationType || undefined,
          status: itemStatus || undefined,
        },
      });
      setItems(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  const loadApprovalStatus = async () => {
    const res = await axios.get(`/variation-batches/${id}/approval/status`);
    setStatus(res.data || null);
  };

  const loadValidationSummary = async () => {
    const res = await axios.get(`/variation-batches/${id}/validation-summary`);
    setValidationSummary(res.data || null);
  };

  const loadConflicts = async () => {
    const res = await axios.get(`/variation-batches/${id}/conflicts`);
    setConflicts(res.data?.conflicts || []);
  };

  const refreshAll = async () => {
    await loadBatch();
    await loadItems();
    await loadApprovalStatus();
    await loadValidationSummary();
    await loadConflicts();
  };

  useEffect(() => {
    refreshAll();
  }, [id]);

  const uploadExcel = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      await axios.post(`/variation-batches/${id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Uploaded and processed successfully. Review the batch items.");
      await refreshAll();
    } catch (error) {
      alert(error?.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`/variation-batches/${id}/submit`);
      alert("Submitted for approval");
      await refreshAll();
    } catch (error) {
      alert(error?.response?.data?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const approve = async () => {
    const comment = window.prompt("Comment (optional):") || "";

    setLoading(true);
    try {
      await axios.post(`/variation-batches/${id}/approval/approve`, { comment });
      alert("Approval recorded");
      await refreshAll();
    } catch (error) {
      alert(error?.response?.data?.message || "Approval failed");
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    const comment = window.prompt("Reason for rejection:") || "";
    if (!comment) return;

    setLoading(true);
    try {
      await axios.post(`/variation-batches/${id}/approval/reject`, { comment });
      alert("Batch rejected");
      await refreshAll();
    } catch (error) {
      alert(error?.response?.data?.message || "Rejection failed");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    const confirmed = window.confirm("Apply this batch? This will update staff records.");
    if (!confirmed) return;

    setLoading(true);
    try {
      await axios.post(`/variation-batches/${id}/apply`);
      alert("Applied successfully");
      await refreshAll();
    } catch (error) {
      alert(error?.response?.data?.message || "Apply failed");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { Header: "PFNO", accessor: "pfno" },
      {
        Header: "Type",
        accessor: "variation_type",
        Cell: TypeCell,
      },
      { Header: "Month", accessor: "effective_month" },
      {
        Header: "Status",
        accessor: "status",
        Cell: StatusCell,
      },
      { Header: "Row", accessor: "source_row" },
      {
        Header: "Error",
        accessor: "validation_error",
        Cell: ErrorCell,
      },
    ],
    []
  );

  if (!batch) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h6">Loading batch...</MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      <MDButton color="dark" size="small" onClick={() => navigate("/variations/batches")}>
        ← Back
      </MDButton>

      <MDBox
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexWrap="wrap"
        gap={2}
      >
        <MDBox>
          <MDTypography variant="h4" fontWeight="bold">
            Batch {batch.batch_ref}
          </MDTypography>

          <MDTypography variant="button" color="text" display="block" mt={0.5}>
            Month: {batch.effective_month} • Status:{" "}
            <span style={{ fontWeight: 700 }}>{batch.status}</span>
          </MDTypography>

          <MDTypography variant="button" color="text" display="block">
            Valid: {batch.valid_items} • Invalid: {batch.invalid_items} • Total: {batch.total_items}
          </MDTypography>
        </MDBox>
      </MDBox>

      <Grid container spacing={3} mt={0.5}>
        <Grid item xs={12} md={3}>
          <SummaryCard
            icon="inventory_2"
            iconColor="info"
            label="Batch Status"
            value={batch.status}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SummaryCard
            icon="check_circle"
            iconColor="success"
            label="Valid Items"
            value={batch.valid_items}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SummaryCard
            icon="error_outline"
            iconColor="error"
            label="Invalid Items"
            value={batch.invalid_items}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SummaryCard
            icon="rule"
            iconColor="warning"
            label="Approval Level"
            value={status?.current_level ?? "Done"}
          />
        </Grid>
      </Grid>

      <MDBox
        mt={3}
        p={2}
        borderRadius="xl"
        sx={{
          background: "#f8fafc",
          border: "1px solid #e5e7eb",
        }}
        display="flex"
        gap={1}
        flexWrap="wrap"
        alignItems="center"
      >
        <MDButton component="label" color="info" disabled={loading || batch.status !== "draft"}>
          Upload Excel
          <input
            hidden
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => uploadExcel(e.target.files?.[0])}
          />
        </MDButton>

        <MDButton color="warning" onClick={submit} disabled={loading || batch.status !== "draft"}>
          Submit for Approval
        </MDButton>

        <MDButton
          color="success"
          onClick={approve}
          disabled={loading || batch.status !== "pending"}
        >
          Approve My Level
        </MDButton>

        <MDButton color="error" onClick={reject} disabled={loading || batch.status !== "pending"}>
          Reject Batch
        </MDButton>

        <MDButton
          color="secondary"
          onClick={apply}
          disabled={loading || batch.status !== "approved"}
        >
          Apply to Payroll Masters
        </MDButton>
      </MDBox>

      <Card
        sx={{
          mt: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
        }}
      >
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="bold">
            Filters
          </MDTypography>

          <MDBox mt={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <MDBox width={180}>
              <MDInput label="PFNO" value={pfno} onChange={(e) => setPfno(e.target.value)} />
            </MDBox>

            <MDBox width={220}>
              <MDInput
                label="Variation Type"
                value={variationType}
                onChange={(e) => setVariationType(e.target.value)}
              />
            </MDBox>

            <MDBox width={160}>
              <MDInput
                label="Item Status"
                value={itemStatus}
                onChange={(e) => setItemStatus(e.target.value)}
              />
            </MDBox>

            <MDButton color="info" onClick={loadItems} disabled={loading}>
              Apply Filters
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      <Card
        sx={{
          mt: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
        }}
      >
        <MDBox p={3}>
          <MDBox
            mb={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <MDTypography variant="h6" fontWeight="bold">
              Variation Items
            </MDTypography>

            <MDTypography variant="button" color="text">
              Showing {items.length} item{items.length === 1 ? "" : "s"}
            </MDTypography>
          </MDBox>

          <DataTable
            table={{ columns, rows: items }}
            entriesPerPage={false}
            showTotalEntries={false}
            canSearch
            pagination
            noEndBorder
          />
        </MDBox>
      </Card>

      {status && (
        <Card
          sx={{
            mt: 3,
            border: "1px solid #e5e7eb",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
          }}
        >
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="bold">
              Approval Status
            </MDTypography>

            <MDBox mt={2} display="flex" gap={4} flexWrap="wrap">
              <MDBox>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  Current Level
                </MDTypography>
                <MDTypography variant="h6">{status.current_level ?? "Completed"}</MDTypography>
              </MDBox>

              <MDBox>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  Fully Approved
                </MDTypography>
                <MDTypography variant="h6" color={status.is_fully_approved ? "success" : "warning"}>
                  {status.is_fully_approved ? "YES" : "NO"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      )}

      {validationSummary && (
        <Card
          sx={{
            mt: 3,
            border: "1px solid #e5e7eb",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
          }}
        >
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="bold">
              Validation Summary
            </MDTypography>

            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={4}>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    border: "1px solid #e5e7eb",
                    background: "#ffffff",
                    minHeight: "100%",
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold">
                    By Type
                  </MDTypography>

                  <MDBox mt={1}>
                    {(validationSummary.by_type || []).length === 0 ? (
                      <MDTypography variant="caption" display="block" color="text">
                        No records
                      </MDTypography>
                    ) : (
                      (validationSummary.by_type || []).map((row, index) => (
                        <MDTypography key={index} variant="caption" display="block">
                          {row.variation_type}: {row.total}
                        </MDTypography>
                      ))
                    )}
                  </MDBox>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    border: "1px solid #e5e7eb",
                    background: "#ffffff",
                    minHeight: "100%",
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold">
                    By Status
                  </MDTypography>

                  <MDBox mt={1}>
                    {(validationSummary.by_status || []).length === 0 ? (
                      <MDTypography variant="caption" display="block" color="text">
                        No records
                      </MDTypography>
                    ) : (
                      (validationSummary.by_status || []).map((row, index) => (
                        <MDTypography key={index} variant="caption" display="block">
                          {row.status}: {row.total}
                        </MDTypography>
                      ))
                    )}
                  </MDBox>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    border: "1px solid #e5e7eb",
                    background: "#ffffff",
                    minHeight: "100%",
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold">
                    Invalid Reasons
                  </MDTypography>

                  <MDBox mt={1}>
                    {(validationSummary.invalid_reasons || []).length === 0 ? (
                      <MDTypography variant="caption" display="block">
                        None
                      </MDTypography>
                    ) : (
                      (validationSummary.invalid_reasons || []).map((row, index) => (
                        <MDTypography key={index} variant="caption" color="error" display="block">
                          {row.validation_error}: {row.total}
                        </MDTypography>
                      ))
                    )}
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      )}

      <Card
        sx={{
          mt: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
        }}
      >
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="bold">
            Conflict Detection
          </MDTypography>

          {conflicts.length === 0 ? (
            <MDBox mt={2}>
              <MDTypography variant="button" color="text">
                No conflicts detected.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox mt={2}>
              {conflicts.map((conflict, index) => (
                <MDBox
                  key={index}
                  mb={1.5}
                  p={2}
                  borderRadius="lg"
                  sx={{
                    border: "1px solid #fecaca",
                    background: "#fff7f7",
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold" color="error">
                    {conflict.pfno} {conflict.message ? `• ${conflict.message}` : ""}
                  </MDTypography>

                  {Array.isArray(conflict.types) && conflict.types.length > 0 && (
                    <MDTypography variant="caption" display="block" mt={0.5}>
                      Types: {conflict.types.join(", ")}
                    </MDTypography>
                  )}

                  {conflict.source_rows && (
                    <MDTypography variant="caption" display="block">
                      Rows:{" "}
                      {Array.isArray(conflict.source_rows)
                        ? conflict.source_rows.join(", ")
                        : conflict.source_rows}
                    </MDTypography>
                  )}
                </MDBox>
              ))}
            </MDBox>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
}
